import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  CatalogFacetCountsResponse,
  CatalogFacets,
  CatalogSort,
  CatalogTagsResponse,
  MediaItem,
  TagFacet,
} from "@gooner/shared";
import { facetValuesToShow, NEW_FACETS,
  clipKey,
  deriveTagDisplayName,
  FACET_ORDER,
  facetLabel,
  facetOf,
  facetValueHint,
  facetValueLabel,
  parseCatalogFacets,
} from "@gooner/shared";
import { api } from "../../lib/api";
import { cx } from "../ui/primitives";
import { ClipCard } from "../../components/ClipCard";
import { ChipGroup, TagChip } from "../components/TagChips";

import { NewBadge } from "../../components/NewBadge";
import { AdSlot } from "../components/AdSlot";
import { BottomSheet } from "../../components/WhatsPlaying";
import { useOnlineRetry } from "../hooks/useOnlineRetry";
import { usePublicSettings } from "../../lib/publicSettings";
import { useSession } from "../../lib/session";
import { BROWSE_AD_CELL_MIN_HEIGHT, browseAdCell } from "../lib/browseAdCell";
import { OTHER_SECTION, mergeUrlOpen, urlOpenGroups } from "../lib/browseOpenGroups";

/** How many clips a visitor scrolls past before the in-feed ad. Deep enough that the
 *  first impression of the page is catalog rather than advertising, shallow enough that
 *  the unit is actually reached. A page shorter than this shows no ad at all. */
const AD_AFTER_TILES = 8;

/** How many tiles load their poster straight away rather than lazily: the fold on a phone
 *  (three rows of two at 390x844 — eager on the first row alone left the rest of the fold
 *  to the lazy loader, behind any preview videos) and on a desktop (two rows of four).
 *  The rest of the grid is lazy. */
const EAGER_TILES = 8;

/**
 * The catalog browser: a sidebar listing every catalog tag (most-used first) beside
 * the catalog grid. Pick a tag to filter the grid to just that tag; with no tag
 * selected the grid is the full catalog. On desktop the tag list is a left column; on
 * mobile it collapses to a horizontal chip strip above the grid. (Collections now live
 * on their own page. A free-text search box lived here too and was deleted, not fixed:
 * clips carry no titles, so it had nothing honest to match.) Anyone can browse,
 * and every card offers the same actions to everyone — signed out, a crown or "+" asks
 * for an account.
 */
export function Browse() {
  // Every filter, the sort, and the page live in the URL — ?tag= / facet keys,
  // ?sort=, ?page= (1-based) — so the sidebar, the grid, deep links, F5, and Back
  // all read from one place (Casey evening shareability Mediums).
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag");
  const facets = useMemo(
    // parseCatalogFacets drops anything outside the declared vocabulary, so a stale
    // bookmark widens the results instead of rendering a filter that can't be cleared.
    () => parseCatalogFacets(Object.fromEntries(params)),
    [params],
  );
  const sort = parseCatalogSortParam(params.get("sort"));
  const page = parseCatalogPageParam(params.get("page"));
  // Fetched here rather than inside the sidebar, because the grid needs the same list:
  // the URL carries a SLUG, and both the sidebar row and the grid's filter pill have
  // to render that slug's display name.
  const tags = useCatalogTags();
  const counts = useFacetCounts(tag, facets);
  const displayName = (slug: string) =>
    tags?.find((t) => t.tag === slug)?.displayName ??
    deriveTagDisplayName(slug);

  // Drop bogus or default page/sort tokens so they do not linger in shared URLs.
  useEffect(() => {
    const rawSort = params.get("sort");
    const rawPage = params.get("page");
    const next = new URLSearchParams(params);
    let dirty = false;
    if (rawSort !== null && (!isCatalogSort(rawSort) || rawSort === DEFAULT_SORT)) {
      next.delete("sort");
      dirty = true;
    }
    if (rawPage !== null) {
      // Keep only page>=2; omit page=1 (default) and drop junk (0, -1, abc).
      if (!/^\d+$/.test(rawPage) || Number(rawPage) < 2) {
        next.delete("page");
        dirty = true;
      }
    }
    if (dirty) setParams(next, { replace: true });
  }, [params, setParams]);

  // Push (don't replace) so Back steps through prior selections and out to the
  // unfiltered grid, which is what putting them in the URL is for.
  const setParam = (key: string, next: string | null) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set(key, next);
      else p.delete(key);
      // Filter change → first page (omit ?page=).
      p.delete("page");
      return p;
    });
  };
  const setTag = (next: string | null) => setParam("tag", next);
  const setFacet = (facet: TagFacet, value: string | null) =>
    // Selecting a second value in the same facet REPLACES the first: one value per
    // facet, so the sidebar row can be read as "the grid is showing this".
    setParam(facet, facets[facet] === value ? null : value);
  const setSort = (next: CatalogSort) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === DEFAULT_SORT) p.delete("sort");
      else p.set("sort", next);
      p.delete("page");
      return p;
    });
  };
  const setPage = useCallback(
    (next: number, opts?: { replace?: boolean }) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next <= 0) p.delete("page");
          else p.set("page", String(next + 1));
          return p;
        },
        { replace: opts?.replace === true },
      );
    },
    [setParams],
  );

  // `pb-28` below `md:` is the Filters button's parking space — without it that fixed
  // control sits on top of the pager at the end of the grid.
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 pb-28 sm:p-6 md:pb-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-100">Browse</h1>
        <p className="text-sm text-zinc-500">
          Dig through every clip in the catalog — filter by tag.
        </p>
      </header>

      <section className="md:flex md:gap-6">
        <TagSidebar
          tags={tags}
          counts={counts}
          facets={facets}
          selected={tag}
          onSelect={setTag}
          onFacet={setFacet}
        />
        {/* The skip link's landing here: past the ~30 filter rows, onto the sort + grid. */}
        <div data-skip-target tabIndex={-1} className="min-w-0 flex-1 scroll-mt-14 outline-none">
          <BrowseCatalog
            category={tag}
            categoryName={tag ? displayName(tag) : null}
            facets={facets}
            sort={sort}
            page={page}
            onCategory={setTag}
            onClearFacet={(f) => setFacet(f, null)}
            onSort={setSort}
            onPage={setPage}
          />
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tag sidebar
// ---------------------------------------------------------------------------

type CatalogTag = CatalogTagsResponse["tags"][number];

/** The catalog's tag vocabulary with counts and resolved display names, most-used
 *  first (the endpoint orders by count desc, then slug). `null` while loading. */
function useCatalogTags(): CatalogTag[] | null {
  const [tags, setTags] = useState<CatalogTag[] | null>(null);
  useEffect(() => {
    api
      .catalogTags("", 500)
      .then((r) => setTags(r.tags))
      .catch(() => setTags([]));
  }, []);
  return tags;
}

type FacetCounts = CatalogFacetCountsResponse["counts"];

/**
 * Per-value counts for every facet under the current filter, refetched whenever the
 * selection changes — the numbers answer "what would I get if I clicked this", so
 * they have to move when anything else moves.
 *
 * The previous counts stay on screen while a new set loads. Blanking them makes the
 * whole column flicker on every click, and a number one selection out of date is
 * better than no number at all.
 */
function useFacetCounts(
  tag: string | null,
  facets: CatalogFacets,
): FacetCounts | null {
  const [counts, setCounts] = useState<FacetCounts | null>(null);
  const key = JSON.stringify([tag, facets]);
  useEffect(() => {
    let ok = true;
    api
      .catalogFacetCounts({ tag: tag ?? undefined, facets })
      .then((r) => {
        if (ok) setCounts(r.counts);
      })
      .catch(() => undefined);
    return () => {
      ok = false;
    };
    // `key` is the serialized filter: facets is a fresh object each render, so
    // depending on it directly would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return counts;
}

/**
 * Facet order in the sidebar, which is not declaration order.
 *
 * What's IN the clip comes before how the file was encoded, because that's the order
 * people actually narrow in — nobody opens the catalog wanting a 4:3 clip. Aspect
 * comes last because it's the one most often ignored entirely.
 */
/**
 * The filter column: closed facet axes, then the open keyword list.
 *
 * The two halves look different because they ARE different. A facet has a fixed
 * vocabulary and takes one value at a time, so it reads as a labelled group of
 * alternatives. The keyword list is open-ended and ordered by popularity, so it stays
 * a plain list. Collapsing them into one flat column — which is what this screen used
 * to be — hides that difference and makes "Latina" look like a synonym for "Amateur".
 *
 * One group list, rendered in two places: a left column from `md:` up, and a bottom
 * sheet behind a "Filters" button below it. It used to be one horizontal strip that
 * swapped to a column via `md:` classes, and at eleven groups that stopped working —
 * roughly a thousand pixels of headers against a 358px phone, so two thirds of the
 * axes sat off-screen with nothing to say they were there, and `min-w-0` (right for
 * the column, where it truncates the summary) let every group in the row shrink to its
 * chevron while eleven nowrap labels painted over each other.
 *
 * Rows show display names; selection reports the SLUG (flat keywords) or the bare
 * value (facets), which is what the URL and the catalog query key on.
 */
function TagSidebar({
  tags,
  counts,
  facets,
  selected,
  onSelect,
  onFacet,
}: {
  tags: CatalogTag[] | null;
  counts: FacetCounts | null;
  facets: CatalogFacets;
  selected: string | null;
  onSelect: (tag: string | null) => void;
  onFacet: (facet: TagFacet, value: string | null) => void;
}) {
  // Everything starts closed, so the list opens as eleven labels rather than the
  // fifty-odd rows they add up to. Opening the one axis you care about is cheap;
  // scrolling past the ten you don't, every visit, is not.
  //
  // EXCEPT a group the URL has a filter for (?race=latina, legacy ?tag=race:latina):
  // that one starts open, and a later URL change opens a group that newly gained a
  // param — see lib/browseOpenGroups. Manual open/close still owns everything else.
  const urlGroups = urlOpenGroups(facets, selected);
  const urlKey = urlGroups.join(",");
  const [open, setOpen] = useState<readonly string[]>(() => urlGroups);
  const prevUrl = useRef<readonly string[]>(urlGroups);
  useEffect(() => {
    const next = urlKey ? urlKey.split(",") : [];
    const prev = prevUrl.current;
    prevUrl.current = next;
    setOpen((o) => mergeUrlOpen(o, prev, next));
  }, [urlKey]);
  const toggle = (key: string) =>
    setOpen((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  // Keywords that belong to a facet are shown BY that facet, so they'd be duplicates
  // here — and worse, misleading ones: picking "Latina" from this list would set
  // ?tag=race:latina, which doesn't compose with the facet filters the way the Race
  // group does.
  const other = tags?.filter((t) => facetOf(t.tag) === null) ?? null;

  const [sheet, setSheet] = useState(false);

  // One list, two mounts — the column and the sheet — so `open` above drives both and a
  // group left open survives dismissing the sheet. Built as a value rather than a
  // component so FACET_ORDER.map stays in this file, which is what clip-length.mts
  // asserts against to keep this surface and the region editor on the shared vocabulary.
  const groups = (
    <div className="flex flex-col gap-4">
      {FACET_ORDER.map((facet) => (
        <FacetGroup
          key={facet}
          facet={facet}
          counts={counts?.[facet]}
          selected={facets[facet] ?? null}
          open={open.includes(facet)}
          onToggle={() => toggle(facet)}
          onSelect={(value) => onFacet(facet, value)}
        />
      ))}

      <ChipGroup
        label="Other"
        // The flat list can hold a selection too, and a closed section that hides
        // an active filter is the one thing collapsing mustn't do.
        summary={
          selected
            ? (other?.find((t) => t.tag === selected)?.displayName ??
              selected)
            : null
        }
        open={open.includes(OTHER_SECTION)}
        onToggle={() => toggle(OTHER_SECTION)}
      >
        {other === null ? (
          <span className="px-1 py-1 text-xs text-zinc-600">
            Loading keywords…
          </span>
        ) : (
          other.map((t) => (
            <TagChip
              key={t.tag}
              label={t.displayName}
              active={selected === t.tag}
              // Clicking the selected keyword clears it — the same toggle the facet
              // chips use. There's no "All" chip to go back to; unselected IS all.
              onClick={() => onSelect(selected === t.tag ? null : t.tag)}
            />
          ))
        )}
      </ChipGroup>
    </div>
  );

  // What the button has to say for itself once the axes are behind it. The pills above
  // the grid still name every active filter; this only has to carry how many.
  const active =
    FACET_ORDER.filter((f) => facets[f]).length + (selected ? 1 : 0);

  return (
    <>
      {/* STICKY ON DESKTOP. The column stays in view while the grid scrolls: `top-16`
          parks it under the sticky site bar (~3.5rem) with a gap, `self-start` stops
          the flex row stretching it to the grid's height (a stretched sticky box has
          nowhere to stick), and the cap + own scroll + overscroll-contain let a long
          column — eleven open groups, then the 600px skyscraper — scroll itself
          without chaining to the page, as SiteBar's menu does. Sticky is bounded by
          the <section>, so it lets go before the footer instead of riding over it.
          Every class is `md:`: below it this column is hidden and the Filters sheet
          is untouched. */}
      <aside
        data-browse-sidebar
        className="hidden md:sticky md:top-16 md:block md:w-56 md:shrink-0 md:self-start md:max-h-[calc(100dvh-5rem)] md:overflow-y-auto md:overscroll-contain"
      >
        {groups}

        {/* Desktop only: below `md` there is no column to put it beside, and a 600px-tall
            skyscraper over a phone's results would be absurd. 160 wide because the column
            is 224 — the usual 300px units do not fit here. */}
        <AdSlot slot="browseSidebar" size={{ width: 160, height: 600 }} className="mt-4" />
      </aside>

      {/* Fixed, not sticky. Sticky would have to sit below the header, and the header's
          height is a measured flex row rather than a declared number — any hardcoded
          offset detaches the moment it grows a line. Fixed needs no such arithmetic, puts
          the control in thumb reach, and is on screen from first paint instead of
          scrolling away with the top of the page. Nothing else floats here: Goon Fuel,
          the only other one, is `hidden lg:block`. */}
      <button
        type="button"
        onClick={() => setSheet(true)}
        aria-expanded={sheet}
        className="fixed bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[var(--color-edge)] bg-[var(--color-panel)]/95 px-5 py-3 text-sm font-medium text-zinc-200 shadow-2xl backdrop-blur transition-colors hover:border-[var(--color-accent)]/60 md:hidden"
      >
        Filters
        {active ? (
          <span className="rounded-full bg-[var(--color-accent)] px-2 py-0.5 text-xs font-semibold text-black tabular-nums">
            {active}
          </span>
        ) : null}
      </button>

      {sheet ? (
        <BottomSheet title="Filters" onClose={() => setSheet(false)}>
          {groups}
        </BottomSheet>
      ) : null}
    </>
  );
}

/**
 * One collapsible facet: its label, and — when open — every declared value, whether
 * or not the current filter leaves any clips in it.
 *
 * Rows carry the keyword's own display name, so "White" under Race but "Medium Tits"
 * under Breasts — TAG_NAMESPACE_SUFFIX already knows which values read as nonsense
 * on their own, and a heading isn't a reason to trust a bare "Medium".
 *
 * A zero-count value stays on screen, dimmed and unclickable, rather than
 * disappearing. The shape of a facet is information — "no trans clip carries a race
 * keyword" is a true and useful thing to be able to see, and a list that silently
 * reflows as you filter is hard to aim at.
 */
function FacetGroup({
  facet,
  counts,
  selected,
  open,
  onToggle,
  onSelect,
}: {
  facet: TagFacet;
  counts: Record<string, number> | undefined;
  selected: string | null;
  open: boolean;
  onToggle: () => void;
  onSelect: (value: string | null) => void;
}) {
  return (
    <ChipGroup
      label={facetLabel(facet)}
      summary={selected ? facetValueLabel(facet, selected) : null}
      open={open}
      onToggle={onToggle}
      badge={NEW_FACETS.includes(facet) && Date.now() < GROUPS_NEW_UNTIL ? <NewBadge variant="inline" /> : null}
    >
      {facetValuesToShow(facet, counts).map((value) => {
        const count = counts?.[value];
        return (
          <TagChip
            key={value}
            label={facetValueLabel(facet, value)}
            hint={facetValueHint(facet, value)}
            active={selected === value}
            // Unavailable, not absent: clicking would empty the grid, so the chip
            // stops responding but stays where it was. A SELECTED chip stays live even
            // at zero — otherwise the only way out would be the grid's pill.
            disabled={count === 0 && selected !== value}
            onClick={() => onSelect(value)}
          />
        );
      })}
    </ChipGroup>
  );
}

/** Until when the keyword groups added 2026-09-12 (NEW_FACETS) wear "New!" here — a
 *  month after launch. Per surface, not shared: see NewBadge's header. */
const GROUPS_NEW_UNTIL = Date.UTC(2026, 9, 13);

// OTHER_SECTION (the non-facet section, a vocabulary nobody chose and no fixed shape)
// lives in lib/browseOpenGroups so the URL-derived expansion can name it.

// ---------------------------------------------------------------------------
// Catalog grid
// ---------------------------------------------------------------------------

/** An active filter above the grid: what it is, what it's set to, and a way out. */
function FilterPill({
  label,
  value,
  onClear,
}: {
  label: string;
  value: string;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-accent)]/50 bg-[var(--color-panel-2)] py-1 pl-3 pr-2 text-xs text-zinc-200">
      <span className="text-zinc-500">{label}:</span>
      <span className="font-medium">{value}</span>
      <button
        type="button"
        aria-label={`Clear ${label} filter`}
        onClick={onClear}
        className="leading-none text-zinc-500 transition-colors hover:text-[var(--color-accent-soft)]"
      >
        ×
      </button>
    </span>
  );
}

const CATALOG_PAGE_SIZE = 30;

const CATALOG_SORTS: ReadonlyArray<{ key: CatalogSort; label: string }> = [
  { key: "latest", label: "Latest" },
  { key: "faps", label: "Most Fapped" },
  { key: "edges", label: "Most Edged" },
  { key: "views", label: "Most Viewed" },
];

/**
 * What the catalog opens on.
 *
 * Most Viewed rather than Latest: the catalog is thousands of clips deep and newest-first
 * shows whatever the last import happened to pull, which is the one ordering nobody chose.
 * What other people actually watched is a better first page for someone who has not
 * filtered anything yet — and the other three orderings are one tap away.
 */
const DEFAULT_SORT: CatalogSort = "views";

const CATALOG_SORT_KEYS = new Set<string>(CATALOG_SORTS.map((s) => s.key));

function isCatalogSort(value: string): value is CatalogSort {
  return CATALOG_SORT_KEYS.has(value);
}

function parseCatalogSortParam(raw: string | null): CatalogSort {
  return raw && isCatalogSort(raw) ? raw : DEFAULT_SORT;
}

/** URL `page` is 1-based for shareable links; returns a 0-based index. Invalid → 0. */
function parseCatalogPageParam(raw: string | null): number {
  if (raw == null || raw === "") return 0;
  if (!/^\d+$/.test(raw)) return 0;
  const n = Number(raw);
  return n < 1 ? 0 : n - 1;
}

function CatalogSortTabs({
  sort,
  onSort,
}: {
  sort: CatalogSort;
  onSort: (s: CatalogSort) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] p-0.5">
      {CATALOG_SORTS.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => onSort(s.key)}
          className={cx(
            // `whitespace-nowrap` because without it "Most Fapped" broke across two
            // lines at 320px and left the row ragged; `pointer-coarse` because 24px is
            // not a tap target.
            "rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors pointer-coarse:py-2",
            sort === s.key
              ? "bg-[var(--color-accent)] text-white"
              : "text-zinc-400 hover:text-zinc-200",
          )}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The catalog grid. `category` (a tag SLUG) is controlled by the parent so the tag
 * sidebar drives it; when it's null the grid shows the full catalog.
 *
 * `categoryName` is the same keyword's display name — the slug queries, the name
 * shows.
 */
function BrowseCatalog({
  category,
  categoryName,
  facets,
  sort,
  page,
  onCategory,
  onClearFacet,
  onSort,
  onPage,
}: {
  category: string | null;
  categoryName: string | null;
  facets: CatalogFacets;
  sort: CatalogSort;
  page: number;
  onCategory: (tag: string | null) => void;
  onClearFacet: (facet: TagFacet) => void;
  onSort: (sort: CatalogSort) => void;
  onPage: (page: number, opts?: { replace?: boolean }) => void;
}) {
  const [items, setItems] = useState<MediaItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry / the online event so a failed fetch can re-run without a full reload.
  const [reloadToken, setReloadToken] = useState(0);
  // Last successful page: a failed Next/Prev must not wipe the grid or drop the pager
  // (Casey midday tip #3 — offline Next left an empty catalog with no Prev/Next).
  const lastGood = useRef<{ page: number; items: MediaItem[]; total: number } | null>(null);
  // Set when a failed flip puts `page` back: that change must not fetch (see the effect).
  const reverted = useRef(false);
  // A flip in flight keeps the old tiles up, so the grid dims to say they are on the way out.
  const [loading, setLoading] = useState(false);

  // Parent clears ?page= when filters/sort change. Drop lastGood so a failed flip
  // cannot restore tiles from a prior filter.
  const facetKey = JSON.stringify(facets);
  useEffect(() => {
    lastGood.current = null;
  }, [category, facetKey, sort]);

  useEffect(() => {
    // A failed flip put `page` back on the tiles already showing. Nothing to fetch, and
    // fetching would clear the error: a Next that failed once then looked like a dead
    // button. Only honoured while lastGood still describes this page — a filter or sort
    // change in the same beat has dropped it and needs its fetch.
    const isRevert = reverted.current && lastGood.current?.page === page;
    reverted.current = false;
    if (isRevert) return;
    let cancelled = false;
    setError(null);
    setLoading(true);
    // Skeleton only when we have nothing good to show. Keep the prior page's tiles
    // visible while a page flip is in flight so a failed Next cannot blank the catalog.
    if (!lastGood.current) setItems(null);
    const filter = category ? { tag: category } : {};
    api
      .searchCatalog({
        ...filter,
        facets,
        sort,
        limit: CATALOG_PAGE_SIZE,
        offset: page * CATALOG_PAGE_SIZE,
      })
      .then((r) => {
        if (cancelled) return;
        setLoading(false);
        setTotal(r.total);
        const maxPage = Math.max(0, Math.ceil(r.total / CATALOG_PAGE_SIZE) - 1);
        if (page > maxPage) {
          // Out-of-range ?page= (e.g. 99999) — clamp in the URL and re-fetch.
          onPage(maxPage, { replace: true });
          return;
        }
        setItems(r.items);
        lastGood.current = { page, items: r.items, total: r.total };
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
        // Friendly copy — never surface raw fetch TypeErrors like "Failed to fetch".
        setError("Couldn't load the catalog.");
        const good = lastGood.current;
        if (good) {
          setItems(good.items);
          setTotal(good.total);
          // Put the page indicator back on the tiles we still have, so Prev/Next stay honest.
          if (page !== good.page) {
            reverted.current = true;
            onPage(good.page, { replace: true });
          }
        } else {
          setItems([]);
          setTotal(0);
        }
      });
    return () => {
      cancelled = true;
    };
    // facetKey stands in for facets: it's a fresh object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort, page, facetKey, reloadToken, onPage]);

  const retry = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);
  useOnlineRetry(retry, error !== null);

  // The in-feed ad's cell is held by the grid itself, decided up front — see
  // lib/browseAdCell for why AdSlot's own mount/collapse must not size it.
  const adSettings = usePublicSettings();
  const sessionReady = useSession((s) => s.ready);
  const goldUntil = useSession((s) => s.user?.goldUntil);
  const adCell = browseAdCell(adSettings, { ready: sessionReady, goldUntil }, Date.now());
  // The one time the cell gives up its floor: the unit never came and AdSlot shows its
  // house card instead, which is a single short row — 270px held open around it was
  // exactly the empty band the card replaces.
  const [adHouse, setAdHouse] = useState(false);
  const onAdHouse = useCallback(() => setAdHouse(true), []);

  const pageCount = Math.max(1, Math.ceil(total / CATALOG_PAGE_SIZE));
  const goToPage = (p: number) => {
    onPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-4">
      {/* Two groups, not one flat wrap. `ml-auto` on the sort tabs only pushes them
          right while they share a line with the pills, and with up to six pills they
          stop sharing one — so the tabs used to land in a different place depending on
          how the filters happened to wrap. Grouped, they get their own line or the far
          end of the row, and nothing else moves them. */}
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div className="flex flex-wrap items-center gap-3">
          {category ? (
            <FilterPill
              label="tag"
              value={categoryName ?? category}
              onClear={() => onCategory(null)}
            />
          ) : null}
          {/* One pill per active facet, each clearable on its own — with up to five
              filters running, "clear everything" is the wrong granularity. */}
          {FACET_ORDER.filter((f) => facets[f]).map((f) => (
            <FilterPill
              key={f}
              label={facetLabel(f).toLowerCase()}
              value={facetValueLabel(f, facets[f] as string)}
              onClear={() => onClearFacet(f)}
            />
          ))}
          {/* The header used to open with the catalog's total ("18425 clips"). Dropped:
              a five-digit number nobody was going to act on. The page indicator stays
              because it is the only thing that says there is more below the fold. */}
          {items && pageCount > 1 ? (
            <span className="text-xs text-zinc-500">
              page {page + 1} of {pageCount}
            </span>
          ) : null}
        </div>
        {/* The four labels must not wrap — that left the control two ragged lines at
            320px — and once they cannot, the segmented control is wider than a 320px
            screen and slid the whole DOCUMENT sideways. So it scrolls inside its own box
            instead. Contained rather than bled to the screen edge, so that at the widths
            where the tabs DO fit nothing reports overflow at all: this page's regression
            signal is "no element paints wider than its box", and a decorative bleed would
            cost that. */}
        <div className="overflow-x-auto md:ml-auto md:overflow-x-visible">
          <CatalogSortTabs
            sort={sort}
            onSort={(s) => {
              lastGood.current = null;
              onSort(s);
            }}
          />
        </div>
      </div>

      {error ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          <span>{error}</span>
          <button
            type="button"
            onClick={retry}
            className="shrink-0 rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Error suppresses the false empty-catalog copy, but not a lastGood grid.
          !113 hid the whole body on any error; !114 restores items in the catch —
          together that wiped tiles while the pager still showed (Casey re-tip). */}
      {items === null && !error ? (
        <GridSkeleton />
      ) : items && items.length > 0 ? (
        <div
          aria-busy={loading}
          className={cx(
            "grid grid-cols-2 gap-3 transition-opacity sm:grid-cols-3 lg:grid-cols-4",
            (loading || !!error) && "opacity-50",
          )}
        >
          {items.map((clip, i) => (
            <Fragment key={clipKey(clip)}>
              {i === AD_AFTER_TILES && adCell !== "none" ? (
                // Two cells wide at every breakpoint. A single cell is 237px once the tag
                // sidebar takes its 224px out of the row (measured, not assumed), which
                // would squeeze a 300px-wide creative; two cells give 486px and the unit
                // centres with room. 250px tall also sits inside the 316px tile row, so
                // the ad never makes its row taller than the clips around it.
                //
                // The CELL is this div, present from the grid's first paint and kept
                // when the zone does not fill: the tiles after it never move.
                <div
                  data-ad-cell={adCell}
                  className="relative col-span-2"
                  style={adHouse ? undefined : { minHeight: BROWSE_AD_CELL_MIN_HEIGHT }}
                >
                  {adCell === "ad" ? (
                    <AdSlot slot="browseInFeed" size={{ width: 300, height: 250 }} onHouse={onAdHouse} fill />
                  ) : null}
                </div>
              ) : null}
              <ClipCard clip={clip} list={items} surface="browse" eager={i < EAGER_TILES} />
            </Fragment>
          ))}
        </div>
      ) : error ? null : (
        <p className="py-16 text-center text-sm text-zinc-500">
          {FACET_ORDER.some((f) => facets[f])
            ? "No clips match these filters."
            : category
              ? `No clips tagged "${categoryName ?? category}".`
              : "The catalog is empty."}
        </p>
      )}

      {items && items.length > 0 && pageCount > 1 ? (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page === 0}
            className="rounded-md border border-[var(--color-edge)] bg-[var(--color-panel)] px-3 py-1.5 text-sm text-zinc-200 transition-colors hover:bg-[var(--color-panel-2)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-xs text-zinc-500">
            {page + 1} / {pageCount}
          </span>
          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= pageCount - 1}
            className="rounded-md border border-[var(--color-edge)] bg-[var(--color-panel)] px-3 py-1.5 text-sm text-zinc-200 transition-colors hover:bg-[var(--color-panel-2)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      ) : null}

    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="aspect-[3/4] animate-pulse rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)]"
        />
      ))}
    </div>
  );
}
